import React, { useState, useCallback, memo } from 'react';
import { prop } from 'ramda';
import { useAppSelector } from 'js/store/hooks';
import { useTranslations } from 'js/hooks/i18n';
import { useEffectOnce } from 'js/hooks/useEffectOnce';

import * as Types from './types';
import { InputPure } from '../Input';
import * as helpers from './helpers';
import { ISO_DATE_FORMAT } from './constants';
import { withFinalFormField } from '../../hocs';

export const DateInputBare: React.FC<Types.IDateInputProps> = ({ value, onChange, ...props }) => {
    const translate = useTranslations();
    const dateFormat = useAppSelector(prop('dateFormat'));
    const [dateString, setDateString] = useState<string>('');
    const preventInput = useCallback(
        (event: React.ClipboardEvent<HTMLInputElement> & React.DragEvent<HTMLInputElement>) => event.preventDefault(), []);

    useEffectOnce(() => {
        const isValidDateNumber = typeof value === 'number' && !Number.isNaN(new Date(value).getTime());
        const isValidDateString = typeof value === 'string' && new Date(value).getTime();

        if (isValidDateNumber || isValidDateString) {
            setDateString(helpers.convertInputDate(
                { value: new Date(value).toISOString(), fromFormat: ISO_DATE_FORMAT, toFormat: dateFormat }
            ));

            onChange && onChange({ target: { value: new Date(value).getTime() || '' } });
        }

        onChange && onChange({ target: { value: '' } });
    }, [value, dateFormat]);

    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const { target } = event;

        setDateString(target.value);

        if (onChange) {
            const date = helpers.convertInputDate(
                { value: target.value, fromFormat: dateFormat, toFormat: ISO_DATE_FORMAT }
            );

            onChange({ target: { value: new Date(helpers.validateDateString(date)).getTime() || '' } });
        }
    }, [dateFormat, onChange]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        const { key } = event;

        if (!new RegExp(/^[0-9]*$/).test(key) && key.length === 1) {
            event.preventDefault();
        }

    }, []);

    const handleOnInput = useCallback(
        (event: React.ChangeEvent<HTMLInputElement> & React.SyntheticEvent<HTMLInputElement, InputEvent>) => {
            const { target, nativeEvent: { inputType } } = event;
            const { separator } = helpers.getDateFormatData(dateFormat);
            let cleanDate = target.value.replaceAll(separator.sign, '');
            let cursorStart = target.selectionStart || 0;

            // insert first separator
            if (cleanDate.length > separator.firstIndex) {
                cleanDate = `${cleanDate.slice(0, separator.firstIndex)}${separator.sign}${cleanDate.slice(separator.firstIndex)}`;
            }

            // insert second separator
            if (cleanDate.length > separator.secondIndex) {
                cleanDate = `${cleanDate.slice(0, separator.secondIndex)}${separator.sign}${cleanDate.slice(separator.secondIndex)}`;
            }

            // if the cursor placed to the left of the separator, after "delete" it should appear to the right of it
            if ((target.selectionStart === separator.firstIndex || cursorStart === separator.secondIndex) && inputType === 'deleteContentForward') {
                cursorStart = cursorStart + 1;
            }

            // correction when adding a number it should be transferred through separator
            if ((cursorStart === separator.firstIndex + 1 || cursorStart === separator.secondIndex + 1) && inputType === 'insertText') {
                cursorStart = cursorStart + 1;
            }

            target.value = cleanDate;
            target.setSelectionRange(cursorStart, cursorStart);

        }, [dateFormat]);

    return (
        <InputPure
            type="text"
            value={dateString}
            inputMode="numeric"
            onCut={preventInput}
            onDrop={preventInput}
            onCopy={preventInput}
            onPaste={preventInput}
            onInput={handleOnInput}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onContextMenu={preventInput}
            maxLength={dateFormat.length}
            placeholder={dateFormat || props.placeholder}
            aria-label={props['aria-label'] || translate('playtech.mobile.system.input.date.aria-label')}
            {...props}
        />
    );
};

export const DateInputPure = memo(DateInputBare);
export const DateInput = withFinalFormField<string>()(DateInputPure);
